import * as React from "react";
import { motion, AnimatePresence } from "motion/react";
import { useContainerSize } from "~/lib/use-container-size";
import { RiAddLine, RiCloseLine } from "@remixicon/react";
import { Button } from "~/components/ui/button";
import { IconButton } from "~/components/ui/icon-button";
import { Picture } from "~/components/ui/picture";

const MotionIconButton = motion.create(IconButton);

export const CARD_HEIGHT = 516;
const OVERLAY_CARD_HEIGHT = 450;
// y-distance the + button travels to the top-right on desktop expand.
const BUTTON_TRAVEL = CARD_HEIGHT - 32 - 32 - 48; // 404px

// Minimum height (px) the image slot must have to be worth displaying in the overlay.
const MIN_IMAGE_HEIGHT = 120;
// Vertical space consumed by the image's own my-6 margins (24px top + 24px bottom).
const IMAGE_MARGINS = 48;

export function InteractiveCard({
  title,
  description,
  cta,
  seeAll,
  bg,
  featureImg,
  expandedWidth,
  isExpanded,
  overlay,
  onToggle,
  itemId,
}: {
  title: string;
  description: string;
  cta: string;
  seeAll: string;
  bg: string;
  featureImg?: string;
  expandedWidth: number;
  isExpanded: boolean;
  overlay: boolean;
  onToggle: () => void;
  /** Stable per-card id used for data-sid; falls back to the title. */
  itemId?: string;
}) {
  // Collapsed: top-darkening gradient keeps title readable.
  const collapsedBg = `linear-gradient(0deg, rgba(92,92,92,0.08) 7.76%, rgba(0,0,0,0.4) 86.98%), linear-gradient(rgba(92,92,92,0.2), rgba(92,92,92,0.2)), ${bg}`;
  // Expanded: diagonal gradient — dark on the left (text), light on the right (image).
  const expandedBg = `linear-gradient(81.67deg, rgba(0,0,0,0.55) 34.2%, rgba(92,92,92,0.11) 90.31%), linear-gradient(rgba(92,92,92,0.2), rgba(92,92,92,0.2)), ${bg}`;

  // Callback refs so useContainerSize re-connects when the overlay mounts/unmounts.
  // contentEl is the padded inner div — ResizeObserver reports content-box height
  // (padding already excluded), so the measurement adapts automatically to any
  // padding value including dynamic env(safe-area-inset-bottom).
  const [contentEl, setContentEl] = React.useState<HTMLDivElement | null>(null);
  const [textEl, setTextEl] = React.useState<HTMLDivElement | null>(null);
  const [btnsEl, setBtnsEl] = React.useState<HTMLDivElement | null>(null);
  const { height: contentH } = useContainerSize(contentEl);
  const { height: textH } = useContainerSize(textEl);
  const { height: btnsH } = useContainerSize(btnsEl);

  // Show the image only when the remaining content-box space is >= MIN_IMAGE_HEIGHT.
  // contentH already excludes padding (content-box), so no magic constants needed.
  const showFeatureImg =
    contentH > 0 &&
    contentH - textH - IMAGE_MARGINS - btnsH >= MIN_IMAGE_HEIGHT;

  // Lock body scroll while the fullscreen overlay is open.
  React.useEffect(() => {
    if (overlay && isExpanded) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [overlay, isExpanded]);

  // The toggle button swaps between two separate IconButton instances (see below) rather
  // than morphing one persistent element, so keyboard focus doesn't survive the swap for
  // free — the focused button literally unmounts. Only restore it if the outgoing button
  // actually had focus (a keyboard interaction); a mouse click shouldn't force a focus jump.
  const toggleBtnRef = React.useRef<HTMLButtonElement>(null);
  const wasToggleFocusedRef = React.useRef(false);
  const handleToggleClick = () => {
    wasToggleFocusedRef.current = document.activeElement === toggleBtnRef.current;
    onToggle();
  };
  React.useEffect(() => {
    if (wasToggleFocusedRef.current) {
      toggleBtnRef.current?.focus();
      wasToggleFocusedRef.current = false;
    }
  }, [isExpanded]);

  // Desktop expanded: image starts at ~55% of the card width.
  const imageLeft = Math.round(expandedWidth * 0.55);

  return (
    <motion.div
      initial={false}
      animate={{ width: isExpanded && !overlay ? expandedWidth : overlay ? 310 : 411 }}
      transition={{ duration: 0.55, ease: [0.4, 0, 0.2, 1] }}
      className="relative shrink-0 overflow-hidden rounded-2xl shadow-sm"
      style={{ height: overlay ? OVERLAY_CARD_HEIGHT : CARD_HEIGHT }}
    >
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ background: collapsedBg, opacity: isExpanded ? 0 : 1 }}
      />
      <div
        className="absolute inset-0 transition-opacity duration-500"
        style={{ background: expandedBg, opacity: isExpanded ? 1 : 0 }}
      />

      {isExpanded && !overlay && (
        <div
          className="pointer-events-none absolute inset-0 rounded-2xl backdrop-blur-[4px]"
          style={{ background: "var(--color-overlay-subtle)" }}
        />
      )}

      <AnimatePresence>
        {!isExpanded && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute top-8 left-8 z-[3]"
          >
            <h3 className={`font-display leading-[115%] font-bold whitespace-pre-line text-white ${overlay ? "text-xl" : "text-display"}`}>
              {title}
            </h3>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isExpanded && !overlay && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, delay: 0.22 }}
            className="absolute inset-0 z-[3] flex flex-col justify-center px-8 py-8"
          >
            <div className="flex w-[537px] flex-col gap-8">
              <div className="flex flex-col gap-6">
                <h3 className="font-display text-display leading-[115%] font-bold whitespace-pre-line text-white">
                  {title}
                </h3>
                <p className="text-md font-sans leading-normal text-neutral-50">
                  {description}
                </p>
              </div>
              <div className="flex w-[181px] flex-col gap-3">
                <Button data-sid={`interactive-card-cta:${itemId ?? title}`} variant="primary-alt" size="default">
                  {cta}
                </Button>
                <Button data-sid={`interactive-card-see-all:${itemId ?? title}`} variant="link" size="default">
                  {seeAll}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {isExpanded && !overlay && (
          <motion.div
            initial={{ opacity: 0, x: 16 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 16, transition: { duration: 0.15, delay: 0 } }}
            transition={{ duration: 0.4, delay: 0.3 }}
            className="pointer-events-none absolute top-0 right-0 bottom-0 z-[2] p-6"
            style={{ left: imageLeft }}
          >
            {featureImg ? (
              <Picture src={featureImg} alt="" className="h-full w-full object-contain" />
            ) : (
              <div className="bg-bouncie-700/30 h-full w-full rounded-2xl" />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {overlay && isExpanded && (
          <motion.div
            key="overlay"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
            className="fixed inset-0 z-50 flex flex-col"
            style={{ background: expandedBg }}
          >
            <div className="pointer-events-none absolute inset-0" style={{ background: "var(--color-overlay)" }} />
            <div
              ref={setContentEl}
              className="relative z-10 flex flex-1 flex-col px-8"
              style={{
                paddingTop: "max(3.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))",
                paddingBottom: "max(3rem, calc(env(safe-area-inset-bottom, 0px) + 2rem))",
              }}
            >
              <div ref={setTextEl} className="flex flex-col gap-4">
                <h3 className="font-display text-xl leading-[115%] font-bold whitespace-pre-line text-white">
                  {title}
                </h3>
                <p className="font-sans text-base leading-normal text-neutral-50">
                  {description}
                </p>
              </div>

              {showFeatureImg && (
                <div className="my-6 max-h-[376px] min-h-0 flex-1 p-6">
                  {featureImg ? (
                    <Picture src={featureImg} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <div className="bg-bouncie-700/40 h-full w-full rounded-2xl" />
                  )}
                </div>
              )}

              <div ref={setBtnsEl} className="mt-auto flex flex-col gap-3">
                <Button data-sid={`interactive-card-compact-cta:${itemId ?? title}`} variant="primary-alt" size="sm">
                  {cta}
                </Button>
                <Button data-sid={`interactive-card-compact-see-all:${itemId ?? title}`} variant="link" size="sm">
                  {seeAll}
                </Button>
              </div>
            </div>

            <MotionIconButton
              variant="glass"
              size="sm"
              icon={RiCloseLine}
              onClick={onToggle}
              aria-label="Close"
              data-sid={`interactive-card-overlay-close:${itemId ?? title}`}
              className="absolute right-8 z-20"
              style={{ top: "max(3.5rem, calc(env(safe-area-inset-top, 0px) + 1rem))" }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
      <motion.div
        className="absolute right-8 bottom-8 z-[4]"
        animate={{ y: isExpanded && !overlay ? -BUTTON_TRAVEL : 0 }}
        transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
      >
        <AnimatePresence mode="wait" initial={false}>
          {isExpanded && !overlay ? (
            <MotionIconButton
              key="x"
              ref={toggleBtnRef}
              variant="glass"
              size={overlay ? "sm" : "default"}
              icon={RiCloseLine}
              onClick={handleToggleClick}
              aria-label="Close"
              data-sid={`interactive-card-close:${itemId ?? title}`}
              initial={{ rotate: -45, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 45, opacity: 0 }}
              transition={{ duration: 0.18 }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
            />
          ) : (
            <MotionIconButton
              key="plus"
              ref={toggleBtnRef}
              variant="primary"
              size={overlay ? "sm" : "default"}
              icon={RiAddLine}
              onClick={handleToggleClick}
              aria-label="Expand"
              data-sid={`interactive-card-expand:${itemId ?? title}`}
              initial={{ rotate: 45, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -45, opacity: 0 }}
              transition={{ duration: 0.18 }}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.94 }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
}
